import { FC, useEffect, useMemo, useState } from "react";
import { useImmer } from "use-immer";
import isAfter from "date-fns/isAfter";
import isBefore from "date-fns/isBefore";
import { useSelector } from "react-redux";
import isSameDay from "date-fns/isSameDay";
import { useTranslation } from "react-i18next";

import styles from "./CreateEventModal.module.scss";
import { withError } from "src/hocs";
import { ConfirmModal } from "src/features";
import { useAppDispatch } from "src/store";
import { useElementFocus, useModal, useTemporaryErrors } from "src/hooks";
import {
  createEvent,
  createWidgetSyncs,
  createDashboardDateRange,
} from "src/store/actions";
import {
  showDevelopmentError,
  generateDashboardDateRangeFromEvent,
  getExistingDashboardDateRangeByEvent,
} from "src/utils";
import {
  EVENT_INPUT_LIMIT,
  EVENT_TYPE_OPTIONS,
  EVENT_DEFAULT_TYPE,
} from "src/constants";
import {
  selectEventsLimit,
  selectActiveWidgetIds,
  selectEventsByDashboardId,
  selectKeywordsDataSourcesLatestDataDate,
  selectTrackersCollectionKeywordsDataSources,
  selectDashboardDataRangeByDashboardDateRangeId,
  selectDashboardDateRangesByTrackersCollectionId,
  selectDefaultDashboardDateRangeByTrackersCollectionId,
} from "src/store/selectors";
import {
  Form,
  Input,
  Label,
  Select,
  Translation,
  DateSelector,
} from "src/components";
import {
  removeExtraSpaces,
  formatToYearMonthDay,
  showToastNotification,
  isEventTypeTypeGuard,
  getDifferenceInObjects,
} from "src/utils";

const InputWithError = withError(Input);

const DateSelectorWithError = withError(DateSelector);

type Props = {
  dashboardId: Dashboard.Data["id"];
  endDate?: DashboardDateRange.Data["endDate"];
  startDate?: DashboardDateRange.Data["startDate"];
  callback?: (value: Event.Data["id"]) => void;
};

export const CreateEventModal: FC<Props> = ({
  callback,
  dashboardId,
  endDate: defaultEndDate,
  startDate: defaultStartDate,
}) => {
  const { t } = useTranslation();

  const { closeModal } = useModal();

  const dispatch = useAppDispatch();

  const [ref, setFocus] = useElementFocus();

  const activeWidgetIds = useSelector(selectActiveWidgetIds);

  const dashboardDateRanges = useSelector((state: Store.RootState) =>
    selectDashboardDateRangesByTrackersCollectionId(state, dashboardId),
  );

  const keywordsDataSources = useSelector((state: Store.RootState) =>
    selectTrackersCollectionKeywordsDataSources(state, dashboardId),
  );

  const keywordsDataSourcesLatestDate = useSelector((state: Store.RootState) =>
    selectKeywordsDataSourcesLatestDataDate(state, keywordsDataSources),
  );

  const dashboardDataRange = useSelector((state: Store.RootState) =>
    selectDefaultDashboardDateRangeByTrackersCollectionId(state, dashboardId),
  );

  const { startDate: dataStartDate } = useSelector((state: Store.RootState) =>
    selectDashboardDataRangeByDashboardDateRangeId(
      state,
      dashboardDataRange?.id || "",
    ),
  );

  const defaultEventType = useMemo<Event.Type>(() => {
    if (
      defaultStartDate &&
      defaultEndDate &&
      defaultEndDate !== defaultStartDate
    )
      return "range";

    return EVENT_DEFAULT_TYPE;
  }, [defaultEndDate, defaultStartDate]);

  const defaultEvent = useMemo<Omit<Event.Data, "id">>(() => {
    let startDate: DashboardDateRange.Data["startDate"];
    let endDate: DashboardDateRange.Data["endDate"];

    if (defaultStartDate && defaultEndDate) {
      startDate = formatToYearMonthDay(defaultStartDate);
      endDate = formatToYearMonthDay(defaultEndDate);
    } else {
      const initialDate = keywordsDataSourcesLatestDate || new Date();

      const formattedInitialDate = formatToYearMonthDay(initialDate);

      startDate = formattedInitialDate;
      endDate = formattedInitialDate;
    }

    return {
      name: "",
      endDate,
      startDate,
      dashboardId,
      authorId: "",
      createdAt: "",
      updatedAt: "",
      companyId: "",
      isActive: true,
      description: "",
      dashboardDateRangeId: "",
    };
  }, [
    dashboardId,
    defaultEndDate,
    defaultStartDate,
    keywordsDataSourcesLatestDate,
  ]);

  const events = useSelector((state: Store.RootState) =>
    selectEventsByDashboardId(state, dashboardId),
  );

  const { isEventsLimitExceeded, eventsLimit } = useSelector(selectEventsLimit);

  const [event, setEvent] = useImmer<Omit<Event.Data, "id">>(defaultEvent);

  const [eventType, setEventType] = useState<Event.Type>(defaultEventType);

  const [loadingStatus, setLoadingStatus] = useState<LoadingStatus>("idle");

  const { errors, setErrors } = useTemporaryErrors(3000);

  const isLoading = useMemo<boolean>(
    () => loadingStatus === "loading",
    [loadingStatus],
  );

  const isSingleDateSelected = useMemo<boolean>(
    () => eventType === "single",
    [eventType],
  );

  const isEventChanged = useMemo<boolean>(
    () => Boolean(getDifferenceInObjects(event, defaultEvent)),
    [event, defaultEvent],
  );

  const isDisabled = useMemo<boolean>(
    () => isLoading || !isEventChanged,
    [isLoading, isEventChanged],
  );

  useEffect(() => setFocus(), [setFocus]);

  const onNameChange = (value: string): void =>
    setEvent((draft) => {
      draft.name = value;
    });

  const onDescriptionChange = (value: string): void =>
    setEvent((draft) => {
      draft.description = value;
    });

  const onTypeChange = (value: string): void => {
    if (!isEventTypeTypeGuard(value)) return;

    setEventType(value);
  };

  const onDateChange = (value: string): void =>
    setEvent((draft) => {
      draft.startDate = value;
      draft.endDate = value;
    });

  const onStartDateChange = (value: string): void => {
    setEvent((draft) => {
      draft.startDate = value;
    });

    const [formattedStartDate, formattedEndDate] = [
      new Date(value),
      new Date(event.endDate),
    ];

    if (!isBefore(formattedStartDate, formattedEndDate))
      setEvent((draft) => {
        draft.endDate = value;
      });
  };

  const onEndDateChange = (value: string): void =>
    setEvent((draft) => {
      draft.endDate = value;
    });

  const onSubmit = async (): Promise<void> => {
    if (isEventsLimitExceeded)
      return showToastNotification({
        id: "event-limit",
        type: "warning",
        text: (
          <Translation
            i18nKey="event.status.warning.event_limit"
            values={{ count: eventsLimit }}
          />
        ),
      });

    const errors = validate();

    if (Object.keys(errors).length) return setErrors(errors);

    const formattedEvent = {
      ...event,
      name: removeExtraSpaces(event.name),
      description: removeExtraSpaces(event.description),
      endDate: eventType === "single" ? event.startDate : event.endDate,
    };

    let createdEventId: Event.Data["id"];

    try {
      setLoadingStatus("loading");

      const existingDashboardDateRangeByEvent =
        getExistingDashboardDateRangeByEvent(event, dashboardDateRanges);

      if (existingDashboardDateRangeByEvent) {
        const createdEvent = await dispatch(
          createEvent({
            ...formattedEvent,
            dashboardDateRangeId: existingDashboardDateRangeByEvent.id,
          }),
        ).unwrap();

        createdEventId = createdEvent.id;
      } else {
        const createdDashboardDateRange = await dispatch(
          createDashboardDateRange(
            generateDashboardDateRangeFromEvent(formattedEvent, dataStartDate),
          ),
        ).unwrap();

        const createdEvent = await dispatch(
          createEvent({
            ...formattedEvent,
            dashboardDateRangeId: createdDashboardDateRange.id,
          }),
        ).unwrap();

        createdEventId = createdEvent.id;

        dispatch(
          createWidgetSyncs({
            forceUpdate: true,
            isScheduled: false,
            dependentWidgetIds: [],
            widgetIds: activeWidgetIds,
            dashboardDateRangeId: createdDashboardDateRange.id,
          }),
        ).catch((error) =>
          showDevelopmentError({
            error,
            additionalTexts: [
              "Error on widget calculation",
              `DashboardDateRangeId: ${createdDashboardDateRange.id}`,
            ],
          }),
        );
      }

      setLoadingStatus("succeeded");

      showToastNotification({
        id: "create_event_success",
        type: "success",
        text: t(
          "component.modal.create_event.form.status.success.event_created",
        ),
      });

      callback?.(createdEventId);

      closeModal("create-event");
    } catch (error) {
      console.error(error);

      showToastNotification({
        type: "error",
        text: t("common.error.server_error"),
      });

      setLoadingStatus("failed");
    }
  };

  function validate() {
    const validationErrors: typeof errors = {};

    const { name, startDate, endDate } = event;

    if (name.trim().length) {
      const isExistingEventName = events.some((event) => event.name === name);

      if (isExistingEventName)
        validationErrors.name = t(
          "component.modal.create_event.form.validation.name_exists",
        );
    } else {
      validationErrors.name = t(
        "component.modal.create_event.form.validation.name_required",
      );
    }

    const [formattedStartDate, formattedEndDate] = [
      new Date(startDate),
      new Date(endDate),
    ];

    const isExistingDateRange = events.some(
      (event) =>
        isSameDay(new Date(event.startDate), formattedStartDate) &&
        isSameDay(new Date(event.endDate), formattedEndDate),
    );

    if (isExistingDateRange) {
      validationErrors.startDate = t(
        "component.modal.create_event.form.validation.date_exists",
      );
      validationErrors.date = t(
        "component.modal.create_event.form.validation.date_exists",
      );
    }

    if (isAfter(formattedStartDate, formattedEndDate)) {
      validationErrors.startDate = t(
        "component.modal.create_event.form.validation.invalid_date",
      );

      validationErrors.endDate = t(
        "component.modal.create_event.form.validation.invalid_date",
      );
    }

    return validationErrors;
  }

  return (
    <ConfirmModal
      id="create-event"
      type="success"
      isLoading={isLoading}
      title={t("component.modal.create_event.title")}
      acceptButton={{
        onClick: onSubmit,
        disabled: isDisabled,
        text: t("component.modal.create_event.button.submit"),
      }}
      cancelButton={{
        onClick: () => closeModal("create-event"),
        text: t("component.modal.create_event.button.cancel"),
      }}
    >
      <Form
        onSubmit={onSubmit}
        disabled={isDisabled}
        className={styles.formWrapper}
      >
        <div className={styles.inputWrapper}>
          <Label leftText={t("component.modal.create_event.form.label.name")} />
          <InputWithError
            ref={ref}
            value={event.name}
            error={errors.name}
            changeHandler={onNameChange}
            characterLimit={EVENT_INPUT_LIMIT}
            placeholder={t(
              "component.modal.create_event.form.placeholder.name",
            )}
          />
        </div>
        <div className={styles.inputWrapper}>
          <Label
            leftText={t("component.modal.create_event.form.label.description")}
          />
          <InputWithError
            value={event.description}
            error={errors.description}
            characterLimit={EVENT_INPUT_LIMIT}
            changeHandler={onDescriptionChange}
            placeholder={t(
              "component.modal.create_event.form.placeholder.description",
            )}
          />
        </div>
        <div className={styles.inputWrapper}>
          <Label
            leftText={t("component.modal.create_event.form.label.event_type")}
          />
          <Select
            value={eventType}
            options={EVENT_TYPE_OPTIONS}
            openingDirection="bottom-end"
            dropdownClassName={styles.selectDropdownWrapper}
            placeholder={t(
              "component.modal.create_event.form.placeholder.event_type",
            )}
            changeHandler={onTypeChange}
          />
        </div>
        {isSingleDateSelected && (
          <div className={styles.inputWrapper}>
            <Label
              leftText={t("component.modal.create_event.form.label.date")}
            />
            <DateSelectorWithError
              error={errors.date}
              value={event.startDate}
              onChange={onDateChange}
              maxValue={keywordsDataSourcesLatestDate}
              placeholder={t(
                "component.modal.create_event.form.placeholder.date",
              )}
            />
          </div>
        )}
        {!isSingleDateSelected && (
          <div className={styles.groupWrapper}>
            <div className={styles.inputWrapper}>
              <Label
                leftText={t(
                  "component.modal.create_event.form.label.start_date",
                )}
              />
              <DateSelectorWithError
                value={event.startDate}
                error={errors.startDate}
                onChange={onStartDateChange}
                maxValue={keywordsDataSourcesLatestDate}
                placeholder={t(
                  "component.modal.create_event.form.placeholder.start_date",
                )}
              />
            </div>
            <div className={styles.inputWrapper}>
              <Label
                leftText={t("component.modal.create_event.form.label.end_date")}
              />
              <DateSelectorWithError
                value={event.endDate}
                error={errors.endDate}
                minValue={event.startDate}
                onChange={onEndDateChange}
                maxValue={keywordsDataSourcesLatestDate}
                placeholder={t(
                  "component.modal.create_event.form.placeholder.endDate",
                )}
              />
            </div>
          </div>
        )}
      </Form>
    </ConfirmModal>
  );
};
